// importFromExternalSystem.js
//
// Anton-review-backfill 2026-04-30 P5h: Tilldelnings-import från fastighetssystem
// med kolumn-mapping. Användaren laddar upp Excel i valfritt format → systemet
// visar rådata-preview → användaren mappar varje AirPark-fält till en Excel-
// kolumn → konvertera till TEMPLATE_COLUMNS-format och återanvänd befintlig
// dry-run / execute-flow.

import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

import Modal from "components/common/modal";
import ButtonText from "components/common/buttonText";

import { formatExistingPermitLabel } from "utils/uploadValidation";

// AirPark-målfält: värde = nyckelnamn i konverterad rad (matchar TEMPLATE_COLUMNS)
const AIRPARK_FIELDS = [
  { key: "Telefonnummer",                  required: true,  type: "phone" },
  { key: "Från-datum",                     required: true,  type: "date"  },
  { key: "Till-datum",                     required: true,  type: "date"  },
  { key: "Regnummer",                      required: false, type: "text"  },
  { key: "Objekt-id",                      required: false, type: "text", note: "antingen detta ELLER Plats-ID" },
  { key: "Plats-ID",                       required: false, type: "text", note: "antingen detta ELLER Objekt-id" },
  { key: "Pris (kr/månad)",                required: false, type: "number", note: "Heltal SEK; tom = 0 kr" },
  { key: "Andrahandsuthyrning (Ja/Nej)",   required: false, type: "yesNo",  note: "Tom = Nej" },
];

const PROFILE_KEY_PREFIX = "airpark.assignment.mappingProfile.";

// Heuristisk auto-mappning: matcha kolumn-namnet mot kända alias.
const COLUMN_ALIASES = {
  "Telefonnummer": [
    "telefonnummer","telefon","tel","mobil","mobile","phone","phonenumber","phone number",
    "hyresgastens telefon","hyresgästens telefon","tenant phone",
  ],
  "Från-datum": [
    "från-datum","fran-datum","från","fran","fromdate","from date","start","start date","startdatum","movein","movein date","move-in date","move in date","inflyttning",
  ],
  "Till-datum": [
    "till-datum","till","todate","to date","slut","slutdatum","end","end date","moveout","moveout date","move-out date","move out date","utflyttning","avtals slut","avtalsslut","slutar",
  ],
  "Regnummer": [
    "regnummer","reg-nr","regnr","reg.nr","reg","plate","license","license plate","licenseplate","registration","fordon","vehicle",
  ],
  "Objekt-id": [
    "objekt-id","objekt id","objekt","objektid","object","objectid","apartment","lägenhet","lgh","lgh-nr","unit","externid",
  ],
  "Plats-ID": [
    "plats-id","platsid","plats","parkeringsplats","parkeringsplats-id","parkingspotextid","parkingspotid","spot id","spotid","parking spot id","parkering",
  ],
  "Pris (kr/månad)": [
    "pris","price","månadspris","manadspris","månadsavgift","manadsavgift","monthly price","monthlyprice","månadskostnad","manadskostnad","hyra","rent","månadshyra","manadshyra","kr/månad","kr per månad","kr/man","kostnad","amount","belopp",
  ],
  "Andrahandsuthyrning (Ja/Nej)": [
    "andrahandsuthyrning","andrahand","andrahandsuthyrn","sublet","subletting","second hand","secondhand","2nd hand","tillåt uthyrning","tillat uthyrning","får hyra ut","far hyra ut","uthyrning","kan hyra ut","kan hyras ut",
  ],
};

function normalizeHeader(h) {
  return (h || "").toString().toLowerCase().trim().replace(/\s+/g, " ");
}

function autoDetectMapping(columns) {
  const mapping = {};
  const used = new Set();
  for (const field of AIRPARK_FIELDS) {
    const aliases = COLUMN_ALIASES[field.key] || [];
    for (const col of columns) {
      const norm = normalizeHeader(col);
      if (used.has(col)) continue;
      if (aliases.includes(norm) || aliases.some(a => norm.includes(a))) {
        mapping[field.key] = col;
        used.add(col);
        break;
      }
    }
  }
  return mapping;
}

// Konvertera olika datum-format till ISO yyyy-mm-dd. Returnerar null om ej parsbart.
export function parseFlexibleDate(value) {
  if (value === null || value === undefined || value === "") return null;

  // Excel-serial-number (när XLSX returnerar datum som number)
  if (typeof value === "number") {
    // Excel epoch = 1899-12-30. JavaScript datum = epoch * 86400000 + (excel - 25569) * 86400000
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    if (!isNaN(date.getTime())) return date.toISOString().slice(0, 10);
  }

  const str = value.toString().trim();
  if (!str) return null;

  // ISO: 2026-05-31
  let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2,"0")}-${m[3].padStart(2,"0")}`;

  // Svenska/EU: 31/05/2026 eller 31-05-2026 eller 31.05.2026
  m = str.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const mon = parseInt(m[2], 10);
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    // Heuristik: om första delen > 12 → måste vara dag-först (EU). Om båda ≤ 12 → anta dag-först (svenska defaulten).
    // Om andra delen > 12 → måste vara mon-först (US). I så fall byter vi.
    if (mon > 12 && day <= 12) {
      const tmp = day; const realDay = mon; const realMon = tmp;
      return `${year}-${String(realMon).padStart(2,"0")}-${String(realDay).padStart(2,"0")}`;
    }
    return `${year}-${String(mon).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
  }

  // Försök med Date.parse som fallback
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);

  return null;
}

// Konvertera varje rad i rådata till AirPark-format med mappningen.
export function convertRowsWithMapping(rawRows, mapping) {
  return (rawRows || []).map((row) => {
    const out = {};
    for (const field of AIRPARK_FIELDS) {
      const sourceCol = mapping[field.key];
      if (!sourceCol) {
        if (field.default !== undefined) out[field.key] = field.default;
        continue;
      }
      const raw = row[sourceCol];
      if (field.type === "date") {
        out[field.key] = parseFlexibleDate(raw);
      } else if (field.type === "phone") {
        out[field.key] = (raw || "").toString().trim();
      } else if (field.type === "number") {
        const n = parseInt((raw || "").toString().trim(), 10);
        out[field.key] = isNaN(n) ? null : n;
      } else if (field.type === "yesNo") {
        const v = (raw == null ? "" : raw.toString()).trim().toLowerCase();
        out[field.key] = (v === "ja" || v === "yes" || v === "y" || v === "1" || v === "true" || v === "sant") ? "Ja" : "";
      } else {
        out[field.key] = (raw || "").toString().trim();
      }
    }
    return out;
  });
}

const ImportFromExternalSystem = ({ display, onClose, onConverted, parkingAreaExtId, spots = [] }) => {
  const { t } = useTranslation();
  const [rawRows, setRawRows] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewError, setPreviewError] = useState(null);

  // localStorage-nyckel per area
  const profileKey = useMemo(() => `${PROFILE_KEY_PREFIX}${parkingAreaExtId || "default"}`, [parkingAreaExtId]);

  // När modalen öppnas — rensa state.
  useEffect(() => {
    if (!display) {
      setRawRows(null);
      setColumns([]);
      setMapping({});
      setPreviewError(null);
    }
  }, [display]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) {
        setPreviewError(t("assignmentImport.emptyFile"));
        return;
      }
      const cols = Object.keys(rows[0]);
      setColumns(cols);
      setRawRows(rows);
      setPreviewError(null);

      // 1. Försök ladda sparad profil för denna area. Profilen sparas med
      // explicit "" för fält som user aktivt valt att inte mappa — vi vill
      // INTE auto-detect:a om profilen explicit har "ej mappad".
      let savedProfile = null;
      try {
        const raw = localStorage.getItem(profileKey);
        if (raw) savedProfile = JSON.parse(raw);
      } catch { /* ignore */ }

      let initial = {};
      const auto = autoDetectMapping(cols);
      for (const f of AIRPARK_FIELDS) {
        if (savedProfile && Object.prototype.hasOwnProperty.call(savedProfile, f.key)) {
          // Profilen styr — även explicit "" (=ej mappad) respekteras.
          if (savedProfile[f.key] && cols.includes(savedProfile[f.key])) {
            initial[f.key] = savedProfile[f.key];
          }
          // Annars lämna ej mappad (lämna initial[f.key] som undefined).
        } else if (auto[f.key]) {
          initial[f.key] = auto[f.key];
        }
      }
      setMapping(initial);
    } catch (err) {
      setPreviewError(t("assignmentImport.parseFailed"));
    }
  };

  const setMappingFor = (fieldKey, value) => {
    setMapping((prev) => ({ ...prev, [fieldKey]: value || undefined }));
  };

  // Validering: alla required måste vara mappade. Antingen Objekt-id eller Parkeringsplats-id måste finnas.
  const validation = useMemo(() => {
    const missing = [];
    for (const f of AIRPARK_FIELDS) {
      if (f.required && !mapping[f.key]) missing.push(f.key);
    }
    const hasIdentifier = mapping["Objekt-id"] || mapping["Plats-ID"];
    if (!hasIdentifier) missing.push("Objekt-id eller Plats-ID");
    return { missing, valid: missing.length === 0 };
  }, [mapping]);

  // Konverterade rader för förhandsgranskning (5 första)
  const convertedPreview = useMemo(() => {
    if (!rawRows || !validation.valid) return [];
    const all = convertRowsWithMapping(rawRows, mapping);
    return all.slice(0, 5);
  }, [rawRows, mapping, validation.valid]);

  const handleConfirm = () => {
    if (!validation.valid || !rawRows) return;
    // Spara profil — inkludera explicit "" för ej-mappade fält så auto-detect
    // inte överskriver vid nästa upload av samma format.
    try {
      const profile = {};
      for (const f of AIRPARK_FIELDS) {
        profile[f.key] = mapping[f.key] || "";
      }
      localStorage.setItem(profileKey, JSON.stringify(profile));
    } catch { /* ignore */ }
    // Konvertera + skicka tillbaka full lista
    const converted = convertRowsWithMapping(rawRows, mapping);
    // Berika med spotInfo om Objekt-id mappad men Plats-ID saknas
    const enriched = converted.map((r) => {
      if (r["Plats-ID"]) return r;
      if (r["Objekt-id"]) {
        const match = spots.find(s => (s.objectID || "").toLowerCase() === r["Objekt-id"].toLowerCase());
        if (match) {
          r["Plats-ID"] = match.parkingSpotExtId;
          // Förifyll Platsnummer + Befintligt tillstånd så enriched matchar mall-format
          r["Platsnummer"] = match.spotIdentifier || "";
          r["Befintligt tillstånd"] = formatExistingPermitLabel(match);
        }
      }
      return r;
    });
    onConverted(enriched);
  };

  return (
    <Modal
      display={display}
      setDisplay={() => onClose()}
      onClose={onClose}
      title={t("assignmentImport.title")}
      innerClassName="!w-[1280px] !max-w-[95vw] bg-white rounded-lg p-6"
    >
      <div className="flex flex-col gap-4" data-testid="assignment-import-block">
        <p className="text-sm text-gray-600">{t("assignmentImport.description")}</p>

        {!rawRows && (
          <div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFile}
              data-testid="assignment-import-file-input"
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-airpark-green file:text-white hover:file:opacity-90"
            />
            {previewError && (
              <p className="mt-2 text-sm text-red-600">{previewError}</p>
            )}
          </div>
        )}

        {rawRows && (
          <>
            <div data-testid="assignment-import-rawpreview">
              <span className="text-sm font-medium text-gray-500 mb-2 block">
                {t("assignmentImport.rawDataPreview", { count: rawRows.length })}
              </span>
              <div className="overflow-x-auto border border-airpark-gray-400 rounded-xl max-h-40">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-airpark-gray-300">
                    <tr>
                      {columns.map((c, i) => (
                        <th key={i} className="px-3 py-2 text-left font-medium">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rawRows.slice(0, 3).map((row, i) => (
                      <tr key={i} className="border-t border-airpark-gray-400">
                        {columns.map((c, ci) => (
                          <td key={ci} className="px-3 py-2">{(row[c] ?? "").toString()}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div data-testid="assignment-import-mapping">
              <span className="text-sm font-medium text-gray-500 mb-2 block">
                {t("assignmentImport.columnMapping")}
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AIRPARK_FIELDS.map((f) => (
                  <label key={f.key} className="flex flex-col text-sm" data-testid={`assignment-import-field-${f.key}`}>
                    <span className="font-medium mb-1">
                      {f.key}
                      {f.required && <span className="text-red-600 ml-1">*</span>}
                      {f.note && <span className="text-gray-500 text-xs ml-1">({f.note})</span>}
                    </span>
                    <select
                      className="border border-airpark-gray-400 rounded-md p-2 bg-white"
                      value={mapping[f.key] || ""}
                      onChange={(e) => setMappingFor(f.key, e.target.value)}
                      data-testid={`assignment-import-select-${f.key}`}
                    >
                      <option value="">{t("assignmentImport.notMapped")}</option>
                      {columns.map((c, i) => (
                        <option key={i} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              {!validation.valid && (
                <div className="mt-2 p-2 rounded text-sm bg-orange-50 border border-orange-200 text-orange-700" data-testid="assignment-import-validation-error">
                  {t("assignmentImport.missingFields", { fields: validation.missing.join(", ") })}
                </div>
              )}
            </div>

            {validation.valid && convertedPreview.length > 0 && (
              <div data-testid="assignment-import-converted-preview">
                <span className="text-sm font-medium text-gray-500 mb-2 block">
                  {t("assignmentImport.convertedPreview")}
                </span>
                <div className="overflow-x-auto border border-airpark-gray-400 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-airpark-gray-300">
                      <tr>
                        {AIRPARK_FIELDS.map((f) => (
                          <th key={f.key} className="px-3 py-2 text-left font-medium">{f.key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {convertedPreview.map((row, i) => (
                        <tr key={i} className="border-t border-airpark-gray-400">
                          {AIRPARK_FIELDS.map((f) => (
                            <td key={f.key} className={`px-3 py-2 ${row[f.key] === null && f.required ? "bg-red-50 text-red-700" : ""}`}>
                              {row[f.key] === null ? "—" : (row[f.key] ?? "").toString()}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end mt-2">
              <ButtonText isSecondary onClick={onClose}>
                {t("generic.cancel")}
              </ButtonText>
              <ButtonText
                onClick={handleConfirm}
                disabled={!validation.valid}
                data-testid="assignment-import-confirm"
              >
                {t("assignmentImport.useMapping")}
              </ButtonText>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ImportFromExternalSystem;
